import { Form, Table } from "antd";
import type { TableProps } from "antd";
import { ComponentType, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "sipli-fleet-core/assets/icons/delete-icon.svg";
import EditIcon from "sipli-fleet-core/assets/icons/edit-icon.svg";

import {
  CustomButton,
  CustomInput,
  CustomModal,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";

import type { PossibleValue } from "src/services/administrationSetupService";

const EditIconComponent = EditIcon as unknown as ComponentType<{
  className?: string;
}>;

const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{
  className?: string;
}>;

interface ParameterPossibleValuesEditorProps {
  values: PossibleValue[];
  onChange: (values: PossibleValue[]) => void;
}

interface PossibleValueForm {
  name: string;
  value: string;
}

type PossibleValueTableRecord = PossibleValue & {
  originalIndex: number;
};

const { CustomText } = CustomTypography;

const ParameterPossibleValuesEditor = ({
  values,
  onChange,
}: ParameterPossibleValuesEditorProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<PossibleValueForm>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const isEditing = editingIndex !== null;

  const handleOpenAddModal = () => {
    setEditingIndex(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (index: number) => {
    setEditingIndex(index);
    form.setFieldsValue({
      name: values[index]?.name ?? "",
      value: values[index]?.value ?? "",
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingIndex(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    const formValues = await form.validateFields();

    const payload = {
      name: formValues.name.trim(),
      value: formValues.value.trim(),
    };

    if (isEditing) {
      onChange(values.map((item, index) => (index === editingIndex ? payload : item)));
    } else {
      onChange([...values, payload]);
    }

    handleCloseModal();
  };

  const handleDelete = (index: number) => {
    onChange(values.filter((_, currentIndex) => currentIndex !== index));
  };

  const tableData = useMemo(
    () => values.map((item, index) => ({ ...item, originalIndex: index })).reverse(),
    [values]
  );

  const columns: TableProps<PossibleValueTableRecord>["columns"] = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      render: (value: string) => value || "--",
    },
    {
      title: t("value"),
      dataIndex: "value",
      key: "value",
      render: (value: string) => value || "--",
    },
    {
      title: t("actions"),
      key: "actions",
      align: "center",
      width: 100,
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2 users-actions">
          <CustomTooltip title={t("edit")}>
            <CustomButton
              type="icon"
              buttonSize="small"
              aria-label={t("edit")}
              icon={<EditIconComponent className="h-[16px] w-[16px]" />}
              className="!bg-transparent active:!bg-transparent focus:!bg-transparent"
              onClick={() => handleOpenEditModal(record.originalIndex)}
            />
          </CustomTooltip>

          <CustomTooltip title={t("delete")}>
            <CustomButton
              type="icon"
              buttonSize="small"
              aria-label={t("delete")}
              icon={<DeleteIconComponent className="h-[16px] w-[15px]" />}
              className="!bg-transparent active:!bg-transparent focus:!bg-transparent"
              onClick={() => handleDelete(record.originalIndex)}
            />
          </CustomTooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="sm:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <CustomText className="!text-sm !font-semibold !text-gray9">
          {t("possible_values")}
        </CustomText>

        <CustomButton
          type="secondary"
          buttonSize="medium"
          label={t("add_possible_value")}
          onClick={handleOpenAddModal}
        />
      </div>

      <Table<PossibleValueTableRecord>
        bordered
        size="small"
        className="!mb-4 !mt-4"
        dataSource={tableData}
        columns={columns}
        rowKey={(record) => String(record.originalIndex)}
        pagination={values.length > 2 ? { pageSize: 2, size: "small" } : false}
        locale={{ emptyText: t("no_records") }}
      />

      <CustomModal
        open={isModalOpen}
        title={isEditing ? t("edit_possible_values") : t("add_possible_values")}
        okText={isEditing ? t("update") : t("add")}
        cancelText={t("cancel")}
        onOk={form.submit}
        onCancel={handleCloseModal}
        destroyOnHidden
        width={520}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className="grid grid-cols-1 gap-x-4 pt-4 sm:grid-cols-2"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label={t("name")}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("name_required"),
              },
            ]}
            className="mb-3!"
          >
            <CustomInput placeholder={t("enter_name")} />
          </Form.Item>

          <Form.Item
            name="value"
            label={t("value")}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("value_required"),
              },
            ]}
            className="mb-3!"
          >
            <CustomInput placeholder={t("enter_value")} />
          </Form.Item>
        </Form>
      </CustomModal>
    </div>
  );
};

export default ParameterPossibleValuesEditor;
